import React, { useContext, useState, useMemo, useCallback, memo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import CreditScoreOutlinedIcon from '@mui/icons-material/CreditScoreOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PlaceIcon from '@mui/icons-material/Place';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import { palette, typography, theme } from '../theme/colors';
import { useB2BAccess, useMultipleModuleAccess } from '../hooks/useModuleAccess';
import { useTierAccess, SubscriptionTier } from '../hooks/useTierAccess';
import { UpgradeModal } from './UpgradeModal';
import { TierBadge } from './subscription/TierBadge';
import { ColorModeContext } from '../contexts/ColorModeContext';
import { useMenuLayout } from '../contexts/MenuLayoutContext';
import { useAnimations } from '../contexts/AnimationsContext';
import { useZoom } from '../contexts/ZoomContext';
import { sections } from '../pages/sections/sectionConfig';
import { usePermissions } from '../hooks/usePermissions';
import type { PermissionResource } from '../types';

const DRAWER_WIDTH_FLAT = 220;
const DRAWER_WIDTH_TABS = 170;

interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: number;
  action?: () => void;
  requiredTier?: SubscriptionTier; // Tier required to access this feature
  requiredPermission?: PermissionResource; // Permission resource required to see this item
  requiredModule?: string; // Module key required to see this item (e.g., 'inventory_management', 'pos')
}

// Colors type for theming
interface SidebarColors {
  bg: string;
  hover: string;
  active: string;
  border: string;
  text: string;
  textActive: string;
  textPrimary: string;
  textMuted: string;
}

// NavItem props interface
interface NavItemProps {
  item: MenuItem;
  itemIndex?: number;
  isActive: boolean;
  isLocked: boolean;
  onNavigate: (path: string) => void;
  onLockedClick: (label: string) => void;
  animationsEnabled: boolean;
  colors: SidebarColors;
}

// NavItem component - moved outside Sidebar to prevent recreation on each render
const NavItem = memo(({ item, itemIndex, isActive, isLocked, onNavigate, onLockedClick, animationsEnabled, colors }: NavItemProps) => {
  const idx = itemIndex ?? 0;
  const active = isActive;

  const handleClick = useCallback(() => {
    if (isLocked) {
      onLockedClick(item.label);
      return;
    }
    if (item.action) {
      item.action();
    } else {
      onNavigate(item.path);
    }
  }, [isLocked, onLockedClick, item, onNavigate]);

  const buttonContent = (
    <ListItemButton
      selected={active && !isLocked}
      onClick={handleClick}
      sx={{
        py: 1,
        px: 1.5,
        mx: 1,
        borderRadius: '6px',
        transition: `all ${theme.transitions.fast}`,
        backgroundColor: active && !isLocked ? alpha(palette.primary, 0.08) : 'transparent',
        opacity: isLocked ? 0.5 : 1,
        '&.Mui-selected': {
          backgroundColor: alpha(palette.primary, 0.08),
        },
        '&:hover': {
          backgroundColor: isLocked ? alpha(palette.warning, 0.05) : alpha(colors.text, 0.04),
          '& .nav-item-icon': {
            color: isLocked ? palette.warning : palette.primary,
          },
          '& .nav-item-label .MuiTypography-root': {
            color: isLocked ? colors.textMuted : colors.textPrimary,
          },
        },
      }}
    >
      {/* Icon Container */}
      <ListItemIcon
        className="nav-item-icon"
        sx={{
          minWidth: 32,
          color: isLocked ? alpha(colors.textMuted, 0.5) : (active ? palette.primary : colors.text),
          transition: `all ${theme.transitions.fast}`,
          '& svg': {
            fontSize: 18,
          },
        }}
      >
        {item.icon}
      </ListItemIcon>

      {/* Label */}
      <ListItemText
        className="nav-item-label"
        primary={item.label}
        primaryTypographyProps={{
          fontSize: '13px',
          fontWeight: active && !isLocked ? typography.fontWeight.semibold : typography.fontWeight.medium,
          fontFamily: typography.fontFamily.body,
        }}
        sx={{
          '& .MuiTypography-root': {
            color: isLocked ? alpha(colors.textMuted, 0.6) : (active ? colors.textPrimary : colors.text),
            transition: `all ${theme.transitions.fast}`,
          },
        }}
      />

      {/* Lock Icon or Badge */}
      {isLocked ? (
        <LockOutlinedIcon
          sx={{
            fontSize: 14,
            color: alpha(palette.warning, 0.7),
          }}
        />
      ) : item.badge !== undefined && item.badge > 0 ? (
        <Box
          sx={{
            minWidth: 20,
            height: 20,
            borderRadius: '10px',
            backgroundColor: palette.error,
            color: '#FFFFFF',
            fontSize: '11px',
            fontWeight: typography.fontWeight.semibold,
            fontFamily: typography.fontFamily.body,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {item.badge}
        </Box>
      ) : null}
    </ListItemButton>
  );

  return (
    <ListItem
      disablePadding
      sx={{
        mb: 0.25,
        ...(animationsEnabled ? {
          opacity: 0,
          animation: 'itemFadeIn 0.2s ease-out forwards',
          animationDelay: `${idx * 0.02}s`,
          '@keyframes itemFadeIn': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        } : {}),
      }}
    >
      {isLocked ? (
        <Tooltip title={`Upgrade to ${item.requiredTier?.toUpperCase()} to unlock`} placement="right">
          {buttonContent}
        </Tooltip>
      ) : (
        buttonContent
      )}
    </ListItem>
  );
});

NavItem.displayName = 'NavItem';

// SectionButton props interface
interface SectionButtonProps {
  sectionId: string;
  label: string;
  index: number;
  isActive: boolean;
  onNavigate: (sectionId: string) => void;
  animationsEnabled: boolean;
  colors: SidebarColors;
}

// SectionButton component - moved outside Sidebar to prevent recreation on each render
const SectionButton = memo(({ sectionId, label, index, isActive, onNavigate, animationsEnabled, colors }: SectionButtonProps) => {
  const active = isActive;

  const handleClick = useCallback(() => {
    onNavigate(sectionId);
  }, [onNavigate, sectionId]);

  return (
    <ListItem
      disablePadding
      sx={{
        mb: 0.5,
        ...(animationsEnabled ? {
          opacity: 0,
          animation: 'fadeIn 0.2s ease-out forwards',
          animationDelay: `${index * 0.05}s`,
          '@keyframes fadeIn': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        } : {}),
      }}
    >
      <ListItemButton
        selected={active}
        onClick={handleClick}
        sx={{
          py: 1.25,
          px: 1.5,
          mx: 1,
          borderRadius: '6px',
          transition: `all ${theme.transitions.fast}`,
          backgroundColor: active ? alpha(palette.primary, 0.08) : 'transparent',
          '&:hover': {
            backgroundColor: alpha(colors.text, 0.04),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(palette.primary, 0.08),
          },
        }}
      >
        {/* Label */}
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: active ? typography.fontWeight.semibold : typography.fontWeight.medium,
            color: active ? colors.textPrimary : colors.text,
            fontFamily: typography.fontFamily.body,
            flex: 1,
            transition: `all ${theme.transitions.fast}`,
          }}
        >
          {label}
        </Typography>

        {/* Chevron */}
        <ChevronRightIcon
          sx={{
            fontSize: 16,
            color: active ? palette.primary : alpha(colors.text, 0.3),
            transition: `all ${theme.transitions.fast}`,
          }}
        />
      </ListItemButton>
    </ListItem>
  );
});

SectionButton.displayName = 'SectionButton';

// Menu item factory functions (use translation keys)
// FREE tier: Dashboard, Products, Inventory, Sales, Staff, Locations
// PRO tier: Reservations, POS, Stock Transfers, B2B features
// ENTERPRISE tier: Advanced features (multi-location already handled by organization limits)

// Helper function to create translated menu items
const createMenuItems = (t: any) => {
  // OPERATIONS - Primary daily tasks
  const operationsItems: MenuItem[] = [
    { label: t('navigation.items.dashboard'), path: '/app', icon: <DashboardOutlinedIcon /> },
    { label: t('navigation.items.pointOfSale'), path: '/app/pos', icon: <PointOfSaleOutlinedIcon />, requiredTier: 'professional', requiredPermission: 'sales', requiredModule: 'pos' },
    { label: t('navigation.items.cashDrawer'), path: '/app/cash-drawer', icon: <PaymentsOutlinedIcon />, requiredPermission: 'cash_drawer' },
    { label: t('navigation.items.credit'), path: '/app/credit', icon: <CreditScoreOutlinedIcon />, requiredPermission: 'credit' },
    { label: t('navigation.items.locations'), path: '/app/locations', icon: <PlaceIcon />, requiredPermission: 'locations', requiredModule: 'location_management' },
  ];

  // SALES & QUOTES - Revenue & B2B transactions
  const salesItems: MenuItem[] = [
    { label: t('navigation.items.orders'), path: '/app/orders', icon: <AssignmentOutlinedIcon />, requiredPermission: 'sales', requiredModule: 'orders' },
    { label: t('navigation.items.pendingPayments'), path: '/app/pending-payments', icon: <HourglassEmptyOutlinedIcon />, requiredPermission: 'sales', requiredModule: 'orders' },
    { label: t('navigation.items.expenses'), path: '/app/expenses', icon: <AccountBalanceWalletOutlinedIcon />, requiredPermission: 'financials', requiredModule: 'sales' },
  ];

  // B2B items shown conditionally in Sales section
  const salesB2BItems: MenuItem[] = [
    { label: t('navigation.items.quotes'), path: '/app/quotes', icon: <RequestQuoteOutlinedIcon />, requiredTier: 'professional', requiredPermission: 'b2b', requiredModule: 'supplier_b2b' },
    { label: t('navigation.items.invoices'), path: '/app/invoices', icon: <ReceiptOutlinedIcon />, requiredTier: 'professional', requiredPermission: 'b2b', requiredModule: 'supplier_b2b' },
  ];

  // CATALOG & STOCK - Products and inventory
  const catalogItems: MenuItem[] = [
    { label: t('navigation.items.products'), path: '/app/products', icon: <CategoryOutlinedIcon />, requiredPermission: 'products', requiredModule: 'inventory_management' },
    { label: t('navigation.items.inventory'), path: '/app/inventory', icon: <InventoryOutlinedIcon />, requiredPermission: 'inventory', requiredModule: 'inventory_management' },
    { label: t('navigation.items.suppliers'), path: '/app/suppliers', icon: <BusinessOutlinedIcon />, requiredPermission: 'inventory', requiredModule: 'inventory_management' },
    { label: t('navigation.items.restocks'), path: '/app/restocks', icon: <ShoppingCartOutlinedIcon />, requiredPermission: 'inventory', requiredModule: 'inventory_management' },
    { label: t('navigation.items.stockTransfers'), path: '/app/stock-transfers', icon: <LocalShippingIcon />, requiredTier: 'professional', requiredPermission: 'inventory', requiredModule: 'location_management' },
    { label: t('navigation.items.deliveries'), path: '/app/deliveries', icon: <LocalShippingIcon />, requiredTier: 'professional', requiredPermission: 'inventory', requiredModule: 'delivery_logistics' },
  ];

  // TEAM & PARTNERS - People management
  // Note: Staff, Roles, and Customers are core features and don't require a specific module
  const teamItems: MenuItem[] = [
    { label: t('navigation.items.staff'), path: '/app/staff', icon: <PeopleOutlinedIcon />, requiredPermission: 'staff' },
    { label: t('navigation.items.roles'), path: '/app/roles', icon: <AdminPanelSettingsOutlinedIcon />, requiredPermission: 'staff' },
    { label: t('navigation.items.customers'), path: '/app/customers', icon: <PersonOutlineIcon />, requiredPermission: 'staff' },
  ];

  // B2B items shown conditionally in Team section
  // Note: Resellers removed - unified Customer entity now handles both individual and business customers
  const teamB2BItems: MenuItem[] = [
    { label: t('navigation.items.accessRequests'), path: '/app/access-requests', icon: <PersonAddOutlinedIcon />, requiredTier: 'professional', requiredPermission: 'b2b', requiredModule: 'supplier_b2b' },
    { label: t('navigation.items.pricingTiers'), path: '/app/pricing-tiers', icon: <LocalOfferOutlinedIcon />, requiredTier: 'professional', requiredPermission: 'b2b', requiredModule: 'supplier_b2b' },
  ];

  // ANALYTICS - Professional analytics and insights
  const analyticsItems: MenuItem[] = [
    { label: t('navigation.items.salesForecast'), path: '/app/analytics/forecast', icon: <TrendingUpOutlinedIcon />, requiredTier: 'professional', requiredPermission: 'reports', requiredModule: 'advanced_analytics' },
    { label: t('navigation.items.demandPlanning'), path: '/app/analytics/demand', icon: <InsightsOutlinedIcon />, requiredTier: 'professional', requiredPermission: 'reports', requiredModule: 'advanced_analytics' },
    { label: t('navigation.items.profitAnalysis'), path: '/app/analytics/profit', icon: <ShowChartOutlinedIcon />, requiredTier: 'professional', requiredPermission: 'reports', requiredModule: 'advanced_analytics' },
  ];

  return { operationsItems, salesItems, salesB2BItems, catalogItems, teamItems, teamB2BItems, analyticsItems };
};

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export const Sidebar = ({ mobileOpen, onMobileClose }: SidebarProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { mode } = useContext(ColorModeContext);
  const { layout } = useMenuLayout();
  const { animationsEnabled } = useAnimations();
  const { zoomLevel } = useZoom();
  const { hasAccess: hasB2BAccess } = useB2BAccess();
  const { tier, hasTierAccess } = useTierAccess();
  const { hasAnyPermission, isAdmin } = usePermissions();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [lockedFeature, setLockedFeature] = useState<string | null>(null);

  // Create translated menu items
  const { operationsItems, salesItems, salesB2BItems, catalogItems, teamItems, teamB2BItems, analyticsItems } = useMemo(
    () => createMenuItems(t),
    [t]
  );

  // Module access check for all relevant modules
  const moduleKeys = [
    'inventory_management',
    'sales',
    'orders',
    'pos',
    'supplier_b2b',
    'location_management',
    'user_management',
    'delivery_logistics',
    'advanced_analytics',
  ];
  const { accessMap: moduleAccessMap, isLoading: modulesLoading } = useMultipleModuleAccess(moduleKeys);

  // Dynamic drawer width based on layout mode
  const DRAWER_WIDTH = layout === 'tabs' ? DRAWER_WIDTH_TABS : DRAWER_WIDTH_FLAT;

  // Calculate drawer height based on zoom level to fill viewport properly
  const drawerHeight = zoomLevel !== 1.0 ? `${100 / zoomLevel}vh` : '100%';

  // Filter menu items based on permissions AND module access
  const filterByPermission = useCallback(
    (items: MenuItem[]): MenuItem[] => {
      return items.filter((item) => {
        // Check module access first (strict enforcement)
        // Only check if modules have loaded and item requires a module
        if (item.requiredModule && !modulesLoading) {
          if (!moduleAccessMap[item.requiredModule]) {
            return false;
          }
        }

        // If no permission required, show the item
        if (!item.requiredPermission) return true;
        // Admins see everything (but still need module access)
        if (isAdmin) return true;
        // Check if user has any permission for this resource
        return hasAnyPermission(item.requiredPermission);
      });
    },
    [isAdmin, hasAnyPermission, moduleAccessMap, modulesLoading]
  );

  // Filtered menu items
  const filteredOperationsItems = useMemo(() => filterByPermission(operationsItems), [filterByPermission, operationsItems]);
  const filteredSalesItems = useMemo(() => filterByPermission(salesItems), [filterByPermission, salesItems]);
  const filteredSalesB2BItems = useMemo(() => filterByPermission(salesB2BItems), [filterByPermission, salesB2BItems]);
  const filteredCatalogItems = useMemo(() => filterByPermission(catalogItems), [filterByPermission, catalogItems]);
  const filteredTeamItems = useMemo(() => filterByPermission(teamItems), [filterByPermission, teamItems]);
  const filteredTeamB2BItems = useMemo(() => filterByPermission(teamB2BItems), [filterByPermission, teamB2BItems]);
  const filteredAnalyticsItems = useMemo(() => filterByPermission(analyticsItems), [filterByPermission, analyticsItems]);

  const settingsItems: MenuItem[] = useMemo(() => {
    const handleLogout = () => {
      localStorage.removeItem('auth');
      navigate('/login');
      onMobileClose();
    };

    return [
      { label: t('navigation.items.settings'), path: '/app/settings', icon: <SettingsOutlinedIcon /> },
      { label: t('navigation.items.logout'), path: '/logout', icon: <LogoutOutlinedIcon />, action: handleLogout },
    ];
  }, [t, navigate, onMobileClose]);

  const colors = mode === 'dark' ? {
    bg: palette.sidebarBg,
    hover: palette.sidebarHover,
    active: palette.sidebarActive,
    border: palette.sidebarBorder,
    text: palette.sidebarText,
    textActive: palette.sidebarTextActive,
    textPrimary: palette.textPrimary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.sidebarBg,
    hover: palette.light.sidebarHover,
    active: palette.light.sidebarActive,
    border: palette.light.sidebarBorder,
    text: palette.light.sidebarText,
    textActive: palette.light.sidebarTextActive,
    textPrimary: palette.light.textPrimary,
    textMuted: palette.light.textMuted,
  };

  const isActive = (path: string) => {
    if (path === '/app') {
      return location.pathname === '/app';
    }
    return location.pathname.startsWith(path);
  };

  // Check if a section is active (for tabs mode)
  const isSectionActive = (sectionId: string) => {
    // Check if we're on a section page
    if (location.pathname.startsWith(`/app/section/${sectionId}`)) {
      return true;
    }
    // Check if we're on a flat route that belongs to this section
    const section = sections.find((s) => s.id === sectionId);
    if (section) {
      return section.tabs.some((tab) => {
        if (tab.flatRoute === '/app') {
          return location.pathname === '/app';
        }
        return location.pathname.startsWith(tab.flatRoute);
      });
    }
    return false;
  };

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
      onMobileClose();
    },
    [navigate, onMobileClose]
  );

  const handleSectionNavigate = useCallback(
    (sectionId: string) => {
      navigate(`/app/section/${sectionId}`);
      onMobileClose();
    },
    [navigate, onMobileClose]
  );

  // Stable handler for locked item clicks
  const handleLockedClick = useCallback(
    (label: string) => {
      setLockedFeature(label);
      setUpgradeModalOpen(true);
    },
    []
  );

  // Tabs mode drawer content - Clean Professional Style
  const tabsModeContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.bg,
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              backgroundColor: palette.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <StorefrontIcon
              sx={{
                color: '#FFFFFF',
                fontSize: 18,
              }}
            />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                fontSize: '15px',
                lineHeight: 1.2,
              }}
            >
              Nexera
            </Typography>
            <Typography
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.body,
                fontSize: '11px',
              }}
            >
              Plateforme
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <TierBadge tier={tier} size="small" showTooltip={true} />
        </Box>
      </Box>

      {/* Navigation Modules */}
      <Box sx={{ flex: 1, py: 2, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        <Typography
          sx={{
            px: 2,
            mb: 1,
            fontFamily: typography.fontFamily.body,
            fontSize: '11px',
            fontWeight: typography.fontWeight.semibold,
            color: colors.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Modules
        </Typography>
        <List disablePadding>
          {sections.map((section, index) => (
            <SectionButton
              key={section.id}
              sectionId={section.id}
              label={section.label}
              index={index}
              isActive={isSectionActive(section.id)}
              onNavigate={handleSectionNavigate}
              animationsEnabled={animationsEnabled}
              colors={colors}
            />
          ))}
        </List>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Settings Section */}
        <Box sx={{ borderTop: `1px solid ${colors.border}`, pt: 2 }}>
          <List disablePadding>
            {settingsItems.map((item, idx) => (
              <NavItem
                key={item.path}
                item={item}
                itemIndex={idx}
                isActive={isActive(item.path)}
                isLocked={!!(item.requiredTier && !hasTierAccess(item.requiredTier))}
                onNavigate={handleNavigate}
                onLockedClick={handleLockedClick}
                animationsEnabled={animationsEnabled}
                colors={colors}
              />
            ))}
          </List>
        </Box>
      </Box>
    </Box>
  );

  // Section Header Component for flat mode
  const SectionHeader = ({ label }: { label: string; moduleNumber: string; itemCount: number }) => (
    <Box
      sx={{
        px: 2,
        pt: 2.5,
        pb: 1,
      }}
    >
      <Typography
        sx={{
          fontFamily: typography.fontFamily.body,
          fontSize: '11px',
          color: colors.textMuted,
          fontWeight: typography.fontWeight.semibold,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  // Flat mode drawer content - Clean Professional Style
  const flatModeContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.bg,
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              backgroundColor: palette.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <StorefrontIcon
              sx={{
                color: '#FFFFFF',
                fontSize: 18,
              }}
            />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                fontSize: '15px',
                lineHeight: 1.2,
              }}
            >
              Nexera
            </Typography>
            <Typography
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.body,
                fontSize: '11px',
              }}
            >
              Plateforme
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <TierBadge tier={tier} size="small" showTooltip={true} />
        </Box>
      </Box>

      {/* Main Navigation - Scrollable */}
      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', pb: 1 }}>
        {/* OPERATIONS Section */}
        {filteredOperationsItems.length > 0 && (
          <>
            <SectionHeader label={t('navigation.sections.operations')} moduleNumber="01" itemCount={filteredOperationsItems.length} />
            <List disablePadding>
              {filteredOperationsItems.map((item, idx) => (
                <NavItem
                  key={item.path}
                  item={item}
                  itemIndex={idx}
                  isActive={isActive(item.path)}
                  isLocked={!!(item.requiredTier && !hasTierAccess(item.requiredTier))}
                  onNavigate={handleNavigate}
                  onLockedClick={handleLockedClick}
                  animationsEnabled={animationsEnabled}
                  colors={colors}
                />
              ))}
            </List>
          </>
        )}

        {/* SALES & QUOTES Section */}
        {(filteredSalesItems.length > 0 || (hasB2BAccess && filteredSalesB2BItems.length > 0)) && (
          <>
            <SectionHeader
              label={t('navigation.sections.salesQuotes')}
              moduleNumber="02"
              itemCount={filteredSalesItems.length + (hasB2BAccess ? filteredSalesB2BItems.length : 0)}
            />
            <List disablePadding>
              {filteredSalesItems.map((item, idx) => (
                <NavItem
                  key={item.path}
                  item={item}
                  itemIndex={filteredOperationsItems.length + idx}
                  isActive={isActive(item.path)}
                  isLocked={!!(item.requiredTier && !hasTierAccess(item.requiredTier))}
                  onNavigate={handleNavigate}
                  onLockedClick={handleLockedClick}
                  animationsEnabled={animationsEnabled}
                  colors={colors}
                />
              ))}
              {hasB2BAccess && filteredSalesB2BItems.map((item, idx) => (
                <NavItem
                  key={item.path}
                  item={item}
                  itemIndex={filteredOperationsItems.length + filteredSalesItems.length + idx}
                  isActive={isActive(item.path)}
                  isLocked={!!(item.requiredTier && !hasTierAccess(item.requiredTier))}
                  onNavigate={handleNavigate}
                  onLockedClick={handleLockedClick}
                  animationsEnabled={animationsEnabled}
                  colors={colors}
                />
              ))}
            </List>
          </>
        )}

        {/* CATALOG & STOCK Section */}
        {filteredCatalogItems.length > 0 && (
          <>
            <SectionHeader label={t('navigation.sections.catalogStock')} moduleNumber="03" itemCount={filteredCatalogItems.length} />
            <List disablePadding>
              {filteredCatalogItems.map((item, idx) => (
                <NavItem
                  key={item.path}
                  item={item}
                  itemIndex={filteredOperationsItems.length + filteredSalesItems.length + (hasB2BAccess ? filteredSalesB2BItems.length : 0) + idx}
                  isActive={isActive(item.path)}
                  isLocked={!!(item.requiredTier && !hasTierAccess(item.requiredTier))}
                  onNavigate={handleNavigate}
                  onLockedClick={handleLockedClick}
                  animationsEnabled={animationsEnabled}
                  colors={colors}
                />
              ))}
            </List>
          </>
        )}

        {/* TEAM & PARTNERS Section */}
        {(filteredTeamItems.length > 0 || (hasB2BAccess && filteredTeamB2BItems.length > 0)) && (
          <>
            <SectionHeader
              label={t('navigation.sections.teamPartners')}
              moduleNumber="04"
              itemCount={filteredTeamItems.length + (hasB2BAccess ? filteredTeamB2BItems.length : 0)}
            />
            <List disablePadding>
              {filteredTeamItems.map((item, idx) => (
                <NavItem
                  key={item.path}
                  item={item}
                  itemIndex={
                    filteredOperationsItems.length +
                    filteredSalesItems.length +
                    (hasB2BAccess ? filteredSalesB2BItems.length : 0) +
                    filteredCatalogItems.length +
                    idx
                  }
                  isActive={isActive(item.path)}
                  isLocked={!!(item.requiredTier && !hasTierAccess(item.requiredTier))}
                  onNavigate={handleNavigate}
                  onLockedClick={handleLockedClick}
                  animationsEnabled={animationsEnabled}
                  colors={colors}
                />
              ))}
              {hasB2BAccess && filteredTeamB2BItems.map((item, idx) => (
                <NavItem
                  key={item.path}
                  item={item}
                  itemIndex={
                    filteredOperationsItems.length +
                    filteredSalesItems.length +
                    (hasB2BAccess ? filteredSalesB2BItems.length : 0) +
                    filteredCatalogItems.length +
                    filteredTeamItems.length +
                    idx
                  }
                  isActive={isActive(item.path)}
                  isLocked={!!(item.requiredTier && !hasTierAccess(item.requiredTier))}
                  onNavigate={handleNavigate}
                  onLockedClick={handleLockedClick}
                  animationsEnabled={animationsEnabled}
                  colors={colors}
                />
              ))}
            </List>
          </>
        )}

        {/* ANALYTICS Section */}
        {filteredAnalyticsItems.length > 0 && (
          <>
            <SectionHeader
              label={t('navigation.sections.analytics')}
              moduleNumber="05"
              itemCount={filteredAnalyticsItems.length}
            />
            <List disablePadding>
              {filteredAnalyticsItems.map((item, idx) => (
                <NavItem
                  key={item.path}
                  item={item}
                  itemIndex={
                    filteredOperationsItems.length +
                    filteredSalesItems.length +
                    (hasB2BAccess ? filteredSalesB2BItems.length : 0) +
                    filteredCatalogItems.length +
                    filteredTeamItems.length +
                    (hasB2BAccess ? filteredTeamB2BItems.length : 0) +
                    idx
                  }
                  isActive={isActive(item.path)}
                  isLocked={!!(item.requiredTier && !hasTierAccess(item.requiredTier))}
                  onNavigate={handleNavigate}
                  onLockedClick={handleLockedClick}
                  animationsEnabled={animationsEnabled}
                  colors={colors}
                />
              ))}
            </List>
          </>
        )}

        {/* Spacer to push Settings section to bottom */}
        <Box sx={{ flex: 1, minHeight: 16 }} />

        {/* Settings Section */}
        <Box sx={{ borderTop: `1px solid ${colors.border}`, pt: 2 }}>
          <List disablePadding>
            {settingsItems.map((item, idx) => (
              <NavItem
                key={item.path}
                item={item}
                itemIndex={99 + idx}
                isActive={isActive(item.path)}
                isLocked={!!(item.requiredTier && !hasTierAccess(item.requiredTier))}
                onNavigate={handleNavigate}
                onLockedClick={handleLockedClick}
                animationsEnabled={animationsEnabled}
                colors={colors}
              />
            ))}
          </List>
        </Box>
      </Box>
    </Box>
  );

  // Select drawer content based on layout mode
  const drawerContent = layout === 'tabs' ? tabsModeContent : flatModeContent;

  return (
    <>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: DRAWER_WIDTH,
            backgroundColor: colors.bg,
            borderRight: 'none',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            height: drawerHeight,
            boxSizing: 'border-box',
            backgroundColor: colors.bg,
            borderRight: `2px solid ${colors.border}`,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Upgrade Modal for locked features */}
      <UpgradeModal
        open={upgradeModalOpen}
        onClose={() => {
          setUpgradeModalOpen(false);
          setLockedFeature(null);
        }}
        feature={lockedFeature || undefined}
        currentTier={tier}
        requiredTier="professional"
      />
    </>
  );
};
